import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useData } from "@/store/DataContext";
import {
  ChipGroup,
  CurrencySegment,
  Field,
  GhostButton,
  PrimaryButton,
  TextArea,
  TextInput,
} from "@/components/base/FormControls";
import PurchaseItemCard, { type ItemDraft } from "./components/PurchaseItemCard";
import { money, number, todayISO, uid } from "@/lib/format";
import type { Currency, Lot, Product, Purchase } from "@/lib/types";

function parse(value: string): number {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

function emptyItem(): ItemDraft {
  return {
    key: uid(),
    name: "",
    category: "",
    quantity: "",
    unitCost: "",
    salePrice: "",
    marginMode: "percent",
    marginValue: "",
    expirationDate: "",
  };
}

export default function NuevaCompra() {
  const { data, addPurchase } = useData();
  const navigate = useNavigate();

  const [date, setDate] = useState(todayISO());
  const [supplier, setSupplier] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [currency, setCurrency] = useState<Currency>(data.settings.presentationCurrency);
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState<ItemDraft[]>([emptyItem()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const productNames = useMemo(() => data.products.map((p) => p.name), [data.products]);

  const totals = useMemo(() => {
    let units = 0;
    let cost = 0;
    let revenue = 0;
    items.forEach((it) => {
      const q = parse(it.quantity);
      units += q;
      cost += q * parse(it.unitCost);
      revenue += q * parse(it.salePrice);
    });
    return {
      units,
      cost,
      revenue,
      avgCost: units > 0 ? cost / units : 0,
      profit: revenue - cost,
    };
  }, [items]);

  const updateItem = (key: string, patch: Partial<ItemDraft>) => {
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  };

  const pickSuggestion = (key: string, name: string) => {
    const product = data.products.find((p) => p.name.toLowerCase() === name.toLowerCase());
    if (!product) {
      updateItem(key, { name });
      return;
    }
    updateItem(key, {
      name: product.name,
      category: product.category,
      salePrice: product.defaultSalePrice ? String(product.defaultSalePrice) : "",
      marginValue: "",
    });
  };

  const loadExample = () => {
    setCurrency("DOP");
    setSupplier("Distribuidora del Caribe");
    setItems([
      {
        key: uid(),
        name: "Bocina grande",
        category: "Audio",
        quantity: "5",
        unitCost: "10",
        salePrice: "15",
        marginMode: "percent",
        marginValue: "",
        expirationDate: "",
      },
      {
        key: uid(),
        name: "Bocina pequeña",
        category: "Audio",
        quantity: "15",
        unitCost: "5",
        salePrice: "8",
        marginMode: "percent",
        marginValue: "",
        expirationDate: "",
      },
    ]);
  };

  const save = async () => {
    const valid = items.filter(
      (it) => it.name.trim() && parse(it.quantity) > 0 && parse(it.unitCost) >= 0,
    );
    if (valid.length === 0) {
      setError("Agrega al menos un producto con nombre, cantidad y costo.");
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const purchaseId = uid();
      const lots: Lot[] = [];
      const newProducts: Product[] = [];
      const creationDate = new Date().toISOString();

      valid.forEach((it) => {
        const name = it.name.trim();
        let product = data.products.find((p) => p.name.toLowerCase() === name.toLowerCase());
        if (!product) {
          product = {
            id: uid(),
            name,
            category: it.category.trim() || "Sin categoría",
            unit: "unidad",
            defaultSalePrice: parse(it.salePrice),
            currency,
            createdAt: creationDate,
          };
          newProducts.push(product);
        }
        lots.push({
          id: uid(),
          productId: product.id,
          productName: product.name,
          purchaseId,
          quantity: parse(it.quantity),
          unitCost: parse(it.unitCost),
          salePrice: parse(it.salePrice),
          currency,
          expirationDate: it.expirationDate || null,
          purchaseDate: date,
        });
      });

      const purchase: Purchase = {
        id: purchaseId,
        type: "purchase",
        date,
        supplier: supplier.trim(),
        invoiceNumber: invoiceNumber.trim(),
        currency,
        exchangeRate: currency === "USD" ? data.settings.exchangeRate : null,
        notes: notes.trim(),
        documentIds: [],
        lotIds: lots.map((l) => l.id),
        createdAt: creationDate,
      };

      await addPurchase(purchase, lots, newProducts);
      navigate("/inventario");
    } catch (err) {
      setError("No se pudo guardar la compra. Intenta de nuevo.");
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fr-animate space-y-5 pb-4">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex h-9 w-9 items-center justify-center rounded-full bg-background-200/80 text-foreground-700"
        >
          <i className="ri-arrow-left-line text-lg" />
        </button>
        <div>
          <h1 className="text-[20px] font-bold text-foreground-950">Registrar compra</h1>
          <p className="text-[12px] text-foreground-500">Agrega productos a tu inventario</p>
        </div>
      </div>

      <div className="rounded-3xl border border-background-200/70 bg-background-50 p-4 shadow-card">
        <div className="space-y-3">
          <Field label="Moneda de la operación">
            <CurrencySegment value={currency} onChange={(v) => setCurrency(v as Currency)} />
          </Field>
          <Field label="Fecha de compra">
            <TextInput value={date} onChange={setDate} type="date" icon="ri-calendar-line" />
          </Field>
          <Field label="Proveedor (opcional)">
            <TextInput
              value={supplier}
              onChange={setSupplier}
              placeholder="Nombre del proveedor"
              icon="ri-store-2-line"
            />
          </Field>
          <Field label="Número de factura (opcional)">
            <TextInput
              value={invoiceNumber}
              onChange={setInvoiceNumber}
              placeholder="Ej. F-0012"
              icon="ri-file-list-3-line"
            />
          </Field>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <h2 className="text-[15px] font-semibold text-foreground-900">Productos de la compra</h2>
        <button
          type="button"
          onClick={loadExample}
          className="rounded-full bg-accent-100 px-3 py-1 text-[11px] font-medium text-accent-900"
        >
          Usar ejemplo bocinas
        </button>
      </div>

      <div className="space-y-3">
        {items.map((it, index) => (
          <PurchaseItemCard
            key={it.key}
            index={index}
            draft={it}
            currency={currency}
            suggestions={productNames}
            onChange={(patch) => updateItem(it.key, patch)}
            onRemove={() => setItems((prev) => prev.filter((x) => x.key !== it.key))}
            onPickSuggestion={(name) => pickSuggestion(it.key, name)}
            canRemove={items.length > 1}
          />
        ))}
      </div>

      <GhostButton
        label="Agregar producto"
        icon="ri-add-line"
        onClick={() => setItems((prev) => [...prev, emptyItem()])}
      />

      <div className="rounded-3xl border border-background-200/70 bg-background-50 p-4 shadow-card">
        <h3 className="mb-3 text-[14px] font-semibold text-foreground-900">Resumen de la compra</h3>
        <div className="space-y-2 text-[13px]">
          <div className="flex justify-between">
            <span className="text-foreground-500">Unidades totales</span>
            <span className="tabular-nums font-medium text-foreground-900">
              {number(totals.units, 0)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-foreground-500">Total de la compra</span>
            <span className="tabular-nums font-semibold text-foreground-950">
              {money(totals.cost, currency)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-foreground-500">Costo promedio por unidad</span>
            <span className="tabular-nums font-medium text-foreground-900">
              {money(totals.avgCost, currency)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-foreground-500">Ingreso potencial</span>
            <span className="tabular-nums font-medium text-foreground-900">
              {money(totals.revenue, currency)}
            </span>
          </div>
          <div className="flex justify-between border-t border-background-200 pt-2">
            <span className="font-medium text-foreground-700">Ganancia bruta estimada</span>
            <span className="tabular-nums font-semibold text-primary-700">
              {money(totals.profit, currency)}
            </span>
          </div>
        </div>
      </div>

      <Field label="Notas adicionales (opcional)">
        <TextArea value={notes} onChange={setNotes} placeholder="Detalles de la compra..." />
      </Field>

      {error ? (
        <p className="rounded-2xl bg-red-50 px-4 py-3 text-[12px] text-red-600">{error}</p>
      ) : null}

      <PrimaryButton
        label={saving ? "Guardando..." : "Guardar compra"}
        icon="ri-save-3-line"
        onClick={save}
        disabled={saving}
      />
    </div>
  );
}